import type { ApprovalChainPortalDto, CollectionItemStatus } from "../api/types";

// Post-Rollout PR 4 (PROB-28) — shared recipient-facing display mapping for
// a CollectionItem status, used by the item-level Outstanding + Documents
// tables so they label states identically.

export function itemStatusLabel(status: CollectionItemStatus): string {
  switch (status) {
    case "Pending":
      return "Not started";
    case "InProgress":
      return "In progress";
    case "SubmittedForReview":
      return "Under review";
    case "ChangesRequested":
      return "Needs changes";
    case "Approved":
      return "Approved";
    case "Waived":
      return "Waived";
  }
}

export type ItemBadgeStatus =
  | "draft"
  | "review"
  | "finalized"
  | "archived"
  | "error"
  | "info"
  | "gold";

export function itemStatusBadge(status: CollectionItemStatus): ItemBadgeStatus {
  switch (status) {
    case "Pending":
      return "archived";
    case "InProgress":
      return "info";
    case "SubmittedForReview":
      return "review";
    case "ChangesRequested":
      return "error";
    case "Approved":
      return "finalized";
    case "Waived":
      return "draft";
  }
}

// State-matched CTA vocabulary for the Outstanding table (PROB-28). The
// label mirrors what the recipient is about to do; the tone drives styling.
// Approval chains (ruling R-C) — a chain item's CTA follows the caller's own
// chain status, never the item status: their turn → "Endorse" / "Sign";
// waiting on another party, or already done → "View".
export function itemActionFor(
  status: CollectionItemStatus,
  approvalChain?: Pick<ApprovalChainPortalDto, "myStatus" | "myAction"> | null,
): { label: string; tone: "primary" | "secondary" | "destructive" } {
  if (approvalChain) {
    return approvalChain.myStatus === "YourTurn"
      ? { label: chainActionLabel(approvalChain.myAction), tone: "primary" }
      : { label: "View", tone: "secondary" };
  }
  switch (status) {
    case "Pending":
      return { label: "Start", tone: "primary" };
    case "InProgress":
      return { label: "Continue", tone: "primary" };
    case "ChangesRequested":
      return { label: "Fix & resubmit", tone: "destructive" };
    case "SubmittedForReview":
    case "Approved":
    case "Waived":
      return { label: "View", tone: "secondary" };
  }
}

// Review loop — the staff note a recipient should see right now, or null.
// The note shows while a response is owed (staff returned the item and the
// recipient hasn't answered), which outlives the ChangesRequested status: the
// first edit after a return moves the item to InProgress while the response
// is still owed. ChangesRequested alone still shows it, so a package that
// never sets `responseOwed` (Report Requests) looks exactly as before. Once
// the item is resubmitted, reopened or approved, nothing stale shows.
//
// Approval chains never show it: a chain return is a party's comment to
// Phoenix carried by the chain, not a staff note owed a response.
export function staffNoteFor(item: {
  status: CollectionItemStatus;
  reviewerNote: string | null;
  responseOwed?: boolean;
  approvalChain?: unknown;
}): string | null {
  if (item.approvalChain) return null;
  const note = item.reviewerNote?.trim();
  if (!note) return null;
  return item.status === "ChangesRequested" || item.responseOwed === true ? note : null;
}

/** "Endorse" / "Sign" — the verb on a chain party's action. */
export function chainActionLabel(action: ApprovalChainPortalDto["myAction"]): string {
  return action === "Endorse" ? "Endorse" : "Sign";
}

/** The Done line on a chain item: what the caller has already done. */
export function chainDoneSentence(action: ApprovalChainPortalDto["myAction"]): string {
  return action === "Endorse"
    ? "You have endorsed this document."
    : "You have signed this document.";
}
